import { html } from 'lit';

/**
 * The few fragments that repeat across views, kept here so a view template stays about its own data.
 *
 * Everything interpolated into a Lit template is escaped, so record text - titles, notes, assignee
 * names - can never be parsed as markup. Do not reach for `unsafeHTML`, and do not set a table column's
 * renderer to `'html'` for a value somebody typed into a form.
 */

/**
 * A string on its way into one of the kit's *trusted HTML* slots.
 *
 * A handful of kit inputs are documented as trusted HTML and rendered through `unsafeHTML` - a form
 * field's `label`, `desc` and `note`, a section title, a card description - because a translation
 * legitimately carries a `<strong>` or a link. That is a fine contract and it is the one hole in "Lit
 * escapes everything": a value that reached such a slot from outside this codebase would be markup.
 *
 * So anything that is *data* goes through here first, and the rule stays checkable rather than
 * remembered: text put in a trusted slot is escaped at the point it is put there. Values that belong in
 * a cell, a heading or a paragraph need none of this - those are ordinary interpolation.
 */
export function plain(value)
{
   return String(value ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;');
}

/** A failed load. `retry` is wired to the view's own reload. */
export function errorPanel(message, retry)
{
   return html`
      <lia-alert
         variant="danger"
         icon="fa-solid fa-triangle-exclamation"
         message=${message}
         .actions=${retry ? [{ id: 'retry', label: 'Retry', variant: 'outline-danger', size: 'sm' }] : []}
         @lia-action=${() => retry?.()}
      ></lia-alert>`;
}

/** The centred spinner a panel shows while its query is running. */
export function loadingPanel(label = 'Running query')
{
   return html`
      <div class="d-flex justify-content-center py-5">
         <lia-spinner label=${label} label-visible></lia-spinner>
      </div>`;
}

/**
 * What a panel shows when a query came back with no rows.
 *
 * The default wording says nothing about *when*, deliberately. Half the sourcetypes here are entities
 * and are listed whole rather than over a window, so "in this time range" would be a false account of
 * the cause on every one of them. `AppView.empty()` is what knows which of the two a panel asked and
 * words it accordingly; this is the fallback for anything rendering outside a view.
 */
export function emptyPanel(message = 'No data.')
{
   return html`<lia-empty-state centered icon="fa-solid fa-inbox" title="Nothing to show" message=${message}></lia-empty-state>`;
}
